import React from 'react';
import { Flex } from '@chakra-ui/react';
import { Helmet } from 'react-helmet';
import { IntlProvider } from 'react-intl';
import { DEFAULT_LOCALE } from '../../constants';
import { SLTConfigProvider } from '../../slt/config/SLTConfigProvider';
import { useSLTAppConfigs } from '../../slt/config/useConfigs';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { EnhancedError } from './EnhancedError';

// <Error> is rendered when:
//
// 1. A user requests a page that is not routable from `app/routes.jsx`.
// 2. A routed component throws an error in `getProps()`.
// 3. A routed component throws an error in `render()`.
//
// It must not throw an error. Keep it as simple as possible.

const Error = (props) => {
    const sltServerConfig = useSLTAppConfigs();

    const {
        env: {
            awsRumApplicationId,
            awsRumEnableMonitoring,
            awsRumGuestRoleARN,
            awsRumIdentityPoolId,
        },
    } = sltServerConfig;

    const gtmId = sltServerConfig?.customPreferences?.[CustomPreference.GOOGLE_TAG_MANAGER_ID];
    return (
        <SLTConfigProvider data={sltServerConfig}>
            <Helmet>
                {String(awsRumEnableMonitoring) === 'true' && (
                    <script type="text/javascript">
                        {`
                            window.awsRumApplicationId='${awsRumApplicationId}';
                            window.awsRumEnableMonitoring='${awsRumEnableMonitoring}';
                            window.awsRumGuestRoleARN='${awsRumGuestRoleARN}';
                            window.awsRumIdentityPoolId='${awsRumIdentityPoolId}';
                            window.turnToSiteKey='${sltServerConfig.env.turntoSiteKey}';
                        `}
                    </script>
                )}
                {/* AWS Rum Monitoring */}
                {String(awsRumEnableMonitoring) === 'true' && (
                    <script type="text/javascript" id="awsRumCode">
                        {`(function(n,i,v,r,s,c,x,z){x=window.AwsRumClient={q:[],n:n,i:i,v:v,r:r,c:c};window[n]=function(c,p){x.q.push({c:c,p:p});};z=document.createElement('script');z.async=true;z.src=s;document.head.insertBefore(z,document.head.getElementsByTagName('script')[0]);})(
                            'cwr',
                            '${awsRumApplicationId}',
                            '1.0.0',
                            'us-west-2',
                            'https://client.rum.us-east-1.amazonaws.com/1.14.0/cwr.js',
                            {
                            sessionSampleRate: 1,
                            guestRoleArn: '${awsRumGuestRoleARN}',
                            identityPoolId: '${awsRumIdentityPoolId}',
                            endpoint: 'https://dataplane.rum.us-west-2.amazonaws.com',
                            telemetries: ['errors','http'],
                            allowCookies: true,
                            enableXRay: false
                            }
                        );`}
                    </script>
                )}

                {/* Google Tag Manager */}
                {gtmId ? (
                    <script
                        src="https://code.jquery.com/jquery-3.7.0.slim.min.js"
                        integrity="sha256-tG5mcZUtJsZvyKAxYLVXrmjKBVLd6VpVccqz/r4ypFE="
                        crossOrigin="anonymous"
                        defer
                    />
                ) : null}
                {gtmId ? (
                    <script>
                        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                        })(window,document,'script','dataLayer','${gtmId}');`}
                    </script>
                ) : null}
            </Helmet>
            {gtmId ? (
                <noscript>
                    <iframe
                        title="GTM iframe"
                        src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
                        height="0"
                        width="0"
                        style={{ display: 'none', visibility: 'hidden' }}
                    />
                </noscript>
            ) : null}
            <Flex id="sf-app" flex={1} direction="column" minWidth="375px">
                <IntlProvider locale={DEFAULT_LOCALE} defaultLocale={DEFAULT_LOCALE}>
                    <EnhancedError error={props} />
                </IntlProvider>
            </Flex>
        </SLTConfigProvider>
    );
};

export default Error;
